import { SyntheticEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MenuItem } from 'primereact/menuitem'
import { TieredMenu } from 'primereact/tieredmenu'

import { Column, Row } from './pure/FlexContainer'
import PrimeIcon, { pi } from './pure/PrimeIcon'
import ProfileAvatar from './ProfileAvatar'
import { useAuthContext } from '../contexts/AuthContext'
import { useArchiveContext } from '../contexts/ArchiveContext'
import { useEulaContext } from '../contexts/EulaContext'
import { useUserContext } from '../contexts/UserContext'
import Organization from '../../models/Organization'
import * as AppTheme from '../../tools/dom/app-theme'
import * as Diagnostics from '../../tools/dom/diagnostics'
import { downloadJson } from '../../tools/dom/download'
import t, { getLanguage, setLanguage } from '../../tools/t'
import DialogContainer from './pure/DialogContainer'
import SupportDialog from './pure/SupportDialog'

export default function UserMenu() {
    const { toggleArchiveMenuItem } = useArchiveContext()
    const { me, selectedOrganization, organizations, selectOrganization } = useUserContext()
    const { eulaExists, showEula } = useEulaContext()
    const { logout } = useAuthContext()
    const [menuOpen, setMenuOpen] = useState(false)
    const [showSupportDialog, setShowSupportDialog] = useState(false)
    const menuRef = useRef<TieredMenu>(null)

    const onChangeOrganization = (organization: Organization) => {
        selectOrganization(organization?.id)
        // Navigate to index causing a page reload if not on a user's profile page, essentially when url like `users/*`
        // This experience supports manually checking user permissions across organizations with minimal navigation.
        // NOTE: This solution is unreliable, as it is impossible to know if the user is even
        //  part of the selected organization before ending up on a non-existent profile page.
        if (!/users\/.+/i.test(location.href)) history.replaceState({}, '', '/')
        location.reload()
    }

    const organizationItems = organizations.map((org) => ({
        label: org.name,
        className: selectedOrganization?.id == org.id ? 'p-highlight' : undefined,
        command: () => onChangeOrganization(org),
    }))

    const items: MenuItem[] = [
        { template: () => <ProfileOverviewMenuItem /> },
        getThemeMenuItem(),
        getLanguageMenuItem(),
        toggleArchiveMenuItem,
        getSupportLinkMenuItem(() => setShowSupportDialog(true)),
        getEulaMenuItem(eulaExists, showEula),
        getDiagnosticsMenuItem(),
        getLogoutMenuItem(logout),
        { separator: true },
        {
            items: organizationItems,
            template: () => (
                <OrganizationMenuItem organizationName={selectedOrganization?.name ?? ''} />
            ),
        },
    ]

    const toggleMenu = (e: SyntheticEvent) => {
        const menu = menuRef.current
        if (menu) menu.toggle(e)
        setMenuOpen(!menuOpen)
    }

    return (
        <>
            <Row
                className="user-menu"
                arrangement="spaced"
                alignment="center"
                gap={0.5}
                onClick={(e) => toggleMenu(e)}
            >
                <Row arrangement="spaced" alignment="center" gap={1}>
                    <ProfileAvatar image={me?.picture} initials={me?.initials} />
                    <Column alignment="start" arrangement="center">
                        <UserMenuHeaderLabel
                            title={me?.name}
                            subtitle={selectedOrganization?.name}
                        />
                    </Column>
                </Row>
                <PrimeIcon name="angle-down" />
                <TieredMenu className="user-menu__popup" ref={menuRef} model={items} popup />
            </Row>
            <SupportDialog onHide={() => setShowSupportDialog(false)} visible={showSupportDialog} />
        </>
    )
}

interface UserMenuHeaderLabelProps {
    title?: string
    subtitle?: string
}
const UserMenuHeaderLabel = ({ title, subtitle }: UserMenuHeaderLabelProps) => (
    <>
        <div className="user-menu__title">{title}</div>
        <div className="user-menu__subtitle">{subtitle}</div>
    </>
)

const ProfileOverviewMenuItem = () => {
    const navigate = useNavigate()
    const { me, selectedOrganization } = useUserContext()
    return (
        <Column
            className="user-menu user-menu--dropdown"
            gap={0.5}
            alignment="center"
            arrangement="center"
            onClick={() => (me ? navigate(me.profileLink) : undefined)}
        >
            <ProfileAvatar image={me?.picture} initials={me?.initials} />
            <Column alignment="center" arrangement="center">
                <UserMenuHeaderLabel title={me?.name} subtitle={selectedOrganization?.name} />
            </Column>
        </Column>
    )
}

const getThemeMenuItem = (): MenuItem => {
    const appTheme = AppTheme.fromStorage()

    const setTheme = (theme?: AppTheme.ThemeType) => {
        // Do not set the selected theme again.
        if (theme === appTheme) return
        if (!theme) AppTheme.unset()
        else AppTheme.set(theme)
        location.reload()
    }

    const getThemeActiveClassName = (theme?: AppTheme.ThemeType) => {
        // Use the same class as Primereact for active styling.
        if (theme === appTheme) return 'p-highlight'
        else return ''
    }

    const getThemeIcon = (theme?: AppTheme.ThemeType) => {
        switch (theme) {
            case 'light':
                return pi('sun')
            case 'dark':
                return pi('moon')
            default:
                return pi('cog')
        }
    }

    const getThemeLabel = (theme?: AppTheme.ThemeType): string => {
        switch (theme) {
            case 'light':
                return t('common_light_theme')
            case 'dark':
                return t('common_dark_theme')
            default:
                return t('common_system_theme')
        }
    }

    return {
        label: getThemeLabel(appTheme),
        icon: getThemeIcon(appTheme),
        items: [
            {
                label: getThemeLabel('light'),
                icon: getThemeIcon('light'),
                className: getThemeActiveClassName('light'),
                command: () => setTheme('light'),
            },
            {
                label: getThemeLabel('dark'),
                icon: getThemeIcon('dark'),
                className: getThemeActiveClassName('dark'),
                command: () => setTheme('dark'),
            },
            {
                label: getThemeLabel(),
                icon: getThemeIcon(),
                className: getThemeActiveClassName(),
                command: () => setTheme(),
            },
        ],
    }
}

const getLanguageMenuItem = (): MenuItem => {
    const selectedLanguage = getLanguage()
    const selectedClassName = (lang: string) =>
        selectedLanguage === lang ? 'p-highlight' : undefined
    return {
        label: t('common_language'),
        icon: pi('language'),
        items: [
            {
                label: 'English',
                className: selectedClassName('en'),
                command: () => setLanguage('en'),
            },
            {
                label: 'Dansk',
                className: selectedClassName('da'),
                command: () => setLanguage('da'),
            },
        ],
    }
}

const getSupportLinkMenuItem = (navigateSupport: () => void) => {
    return {
        label: t('menu_item_support'),
        icon: pi('link'),
        command: navigateSupport,
    }
}

const getEulaMenuItem = (eulaExists: boolean, showEula: () => void) => {
    return {
        visible: eulaExists,
        label: t('menu_item_eula'),
        icon: pi('exclamation-circle'),
        command: showEula,
    }
}

const getDiagnosticsMenuItem = () => {
    const dumpDiagnostics = () => downloadJson(Diagnostics.label(), Diagnostics.dump())
    return {
        label: t('menu_item_dump_diagnostics'),
        icon: pi('download'),
        command: dumpDiagnostics,
    }
}

const getLogoutMenuItem = (logout: () => void) => {
    return {
        label: t('common_logout'),
        icon: pi('sign-out'),
        command: logout,
    }
}

interface OrganizationMenuProps {
    organizationName: string
}
const OrganizationMenuItem = ({ organizationName }: OrganizationMenuProps) => {
    return (
        <Column className="organization-select" gap={0.25}>
            <div className="organization-select__label">{t('organization_switch')}</div>
            <Row gap={0.75} alignment="center">
                <Column
                    arrangement="center"
                    alignment="center"
                    className="organization-select__icon"
                >
                    <PrimeIcon name="building" size={16} />
                </Column>
                <Row grow arrangement="spaced" alignment="center">
                    <Column>
                        <UserMenuHeaderLabel
                            title={organizationName}
                            subtitle={t('common_organization')}
                        />
                    </Column>
                    <PrimeIcon className="user-menu__chevron" name="chevron-down" size={12} />
                </Row>
            </Row>
        </Column>
    )
}
